import type { DataGridColumn } from '../../types';
import { COLORS } from './styles';

/** Row height (points) reserved for vertical (rotated) headers. */
const ROTATED_HEADER_HEIGHT = 90;

/**
 * Style the header row (row 1) : bold white font on the primary blue
 * background, centered, bordered, and with an explicit row height so it
 * stands out as a proper "table header".
 *
 * `columns` (in export order) lets individual columns opt into a rotated
 * (vertical) header via `col.headerRotate` — useful for narrow columns
 * like 30-minute time slots where a horizontal label would force the
 * sheet absurdly wide. When at least one column rotates, the header row
 * grows tall enough to fit the vertical text.
 */
export function styleHeaderRow<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sheet: any,
  columns: DataGridColumn<T>[]
): void {
  const headerRow = sheet.getRow(1);
  const hasRotatedHeader = columns.some((col) => col.headerRotate);
  headerRow.height = hasRotatedHeader ? ROTATED_HEADER_HEIGHT : 22;
  headerRow.font = { bold: true, color: { argb: COLORS.headerFont } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: COLORS.headerFill },
  };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
  headerRow.eachCell((c: { border: unknown }) => {
    c.border = {
      top: { style: 'thin', color: { argb: COLORS.headerBorder } },
      left: { style: 'thin', color: { argb: COLORS.headerBorder } },
      bottom: { style: 'thin', color: { argb: COLORS.headerBorder } },
      right: { style: 'thin', color: { argb: COLORS.headerBorder } },
    };
  });

  columns.forEach((col, idx) => {
    if (!col.headerRotate) return;
    const cell = headerRow.getCell(idx + 1);
    cell.alignment = { textRotation: 90, vertical: 'middle', horizontal: 'center' };
  });
}

/**
 * Apply the banded-row zebra fill on plain data rows and draw hair-thin
 * borders on every cell. Group headers and subtotals are detected via
 * their pre-existing `fill.type` — we skip them so their custom styling
 * stays intact.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyBandedRows(sheet: any): void {
  const totalRows = sheet.rowCount;
  for (let r = 2; r <= totalRows; r++) {
    const row = sheet.getRow(r);
    const rowFill = row.fill as { type?: string } | undefined;
    const shouldBand = !rowFill?.type && (r - 1) % 2 === 0;
    row.eachCell(
      { includeEmpty: true },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (c: any) => {
        // Zebra fill applique cell-par-cell (pas au niveau row) pour ne PAS
        // ecraser les cell.fill deja poses par le writer (ex slot vert/rouge
        // des matrices US-C). Un `row.fill` type:solid ecraserait sinon toute
        // la ligne au moment du render Excel, meme si `cell.fill` etait defini.
        if (shouldBand && !c.fill?.type) {
          c.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: COLORS.bandedRowFill },
          };
        }
        c.border = {
          top: { style: 'hair', color: { argb: COLORS.rowBorder } },
          left: { style: 'hair', color: { argb: COLORS.rowBorder } },
          bottom: { style: 'hair', color: { argb: COLORS.rowBorder } },
          right: { style: 'hair', color: { argb: COLORS.rowBorder } },
        };
      }
    );
  }
}

/**
 * Set each column's width to fit its widest cell content, with sensible
 * min / max caps. Formula cells report `result` as their value — we read
 * that instead of the `{ formula: … }` wrapper object.
 *
 * `exportCols` (optional) — quand fourni, on skip les colonnes `dataType:
 * 'slot'` : leur largeur cible est très fine (matrice 30 min US-C, cf
 * `build-excel.ts` init) et l'autosize sinon les élargit à `min 12` par
 * défaut, ruinant la densité voulue.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function autoSizeColumns(sheet: any, exportCols?: readonly { dataType?: string }[]): void {
  const isSlotIndex = new Set<number>();
  if (exportCols) {
    exportCols.forEach((col, i) => {
      if (col.dataType === 'slot') isSlotIndex.add(i);
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sheet.columns.forEach((col: any, idx: number) => {
    if (isSlotIndex.has(idx)) return; // width déjà positionné à 3 par le caller
    let maxLen = (col.header ?? '').toString().length;
    col.eachCell?.({ includeEmpty: true }, (cell: { value: unknown }) => {
      const v = cell.value;
      const s =
        v == null
          ? ''
          : typeof v === 'object' && 'formula' in (v as object)
            ? String((v as { result?: unknown }).result ?? '')
            : String(v);
      if (s.length > maxLen) maxLen = s.length;
    });
    col.width = Math.min(Math.max(maxLen + 3, 12), 60);
  });
}
